import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as MuiThemeProvider, Theme } from "@mui/material/styles";
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { putUser } from "../api/user";
import {
	GlobalStateActions,
	useGlobalState,
	useGlobalStateDispatch,
} from "../components/globalState";
import { Mode, setTheme } from "../components/theme";
import { cacheThemeMode, readCachedThemeMode } from "./theme-mode-cache";

interface ThemeContextValue {
	theme: Theme;
	mode: Mode;
	toggleTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
	const globalState = useGlobalState();
	const dispatch = useGlobalStateDispatch();
	const currentUser = globalState?.currentUser;

	// The account's mode once the user has loaded; until then this browser's
	// cached copy, so a dark user does not see a light flash on reload.
	const initialMode =
		(currentUser?.userprofile?.config?.profile?.theme as Mode) ||
		readCachedThemeMode() ||
		"light";
	const [mode, setMode] = useState<Mode>(initialMode);

	// Sync mode when currentUser changes (e.g., on initial load)
	useEffect(() => {
		const userTheme = currentUser?.userprofile?.config?.profile?.theme as Mode;
		if (userTheme) {
			cacheThemeMode(userTheme);
			if (userTheme !== mode) {
				setMode(userTheme);
			}
		}
	}, [currentUser?.userprofile?.config?.profile?.theme]);

	// Create MUI theme object
	const theme = useMemo(() => setTheme(mode), [mode]);

	// Toggle theme function
	const toggleTheme = useCallback(async () => {
		if (!currentUser) return;

		const newMode: Mode = mode === "dark" ? "light" : "dark";
		setMode(newMode);
		cacheThemeMode(newMode);

		// Update user config
		const updatedUser = {
			...currentUser,
			userprofile: {
				...currentUser.userprofile,
				config: {
					...currentUser.userprofile.config,
					profile: {
						...currentUser.userprofile.config.profile,
						theme: newMode,
					},
				},
			},
		};

		// Persist to backend
		try {
			await putUser(updatedUser);
			// Update GlobalState so other components see the change
			dispatch({
				type: GlobalStateActions.UPDATE_USER,
				data: { currentUser: updatedUser },
			});
		} catch (error) {
			// Revert on failure
			console.error("Failed to save theme preference:", error);
			setMode(mode);
			cacheThemeMode(mode);
		}
	}, [currentUser, mode, dispatch]);

	// Keyboard shortcut: Ctrl+Alt+T
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.ctrlKey && event.altKey && event.key.toLowerCase() === "t") {
				event.preventDefault();
				toggleTheme();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [toggleTheme]);

	const value = useMemo(
		() => ({ theme, mode, toggleTheme }),
		[theme, mode, toggleTheme]
	);

	return (
		<ThemeContext.Provider value={value}>
			<MuiThemeProvider theme={theme}>
				<CssBaseline />
				{children}
			</MuiThemeProvider>
		</ThemeContext.Provider>
	);
};

export const useThemeContext = (): ThemeContextValue => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useThemeContext must be used within a ThemeProvider");
	}
	return context;
};
